import { FC } from "preact/compat";
import { Decimal } from "@kase/my-marathon-communication";
import { useSignal } from "@preact/signals";
import { useApp } from "~/core";
import { Backdrop } from "~/components";
import {
    ApprovalColor,
    approvalKindString,
    cmpEmployee,
    formatIDecimal,
    getApprovalColor,
    toSortedApprovals,
} from "~/routes/admin-approval/utils";
import ApproverAvatar from "./ApproverAvatar";
import { ApproversProps, BudgetApproval } from "./interfaces";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const MAX_DISPLAY = 3;
const POPUP_WIDTH = 40 * 16; // 40rem
const POPUP_MAX_HEIGHT = 10 * 16; // 10rem

const TEXT_CLASS: Record<ApprovalColor, string> = {
    gray: "text-gray-600",
    blue: "text-blue-600",
    green: "text-green-600",
    red: "text-red-600",
    transparent: "text-gray-400",
};

const Approvers: FC<ApproversProps> = ({
    approvals,
    amount,
    loading = false,
    mobile,
    emptyLabel = "-",
}) => {
    const { api } = useApp();
    const expanded = useSignal(false);
    const rect = useSignal({
        top: mobile ? undefined : 0,
        right: mobile ? undefined : 0,
        bottom: mobile ? 60 : undefined,
        left: mobile ? 8 : undefined,
    });
    const items = approvals.filter(
        (a) => !cmpEmployee(a, api.employee.me.value) || a.kind !== 0,
    );
    const sorted = toSortedApprovals(items).slice(
        0,
        MAX_DISPLAY,
    ) as BudgetApproval[];

    const getUser = (approval: BudgetApproval) =>
        api.employee.get(approval.approver);

    const getUserFullname = (approval: BudgetApproval) =>
        getUser(approval)?.name || approval.email || "Unknown";

    const renderUserAvatar = (approval: BudgetApproval) => {
        const user = getUser(approval);

        if (!user) {
            if (!approval.email) {
                return null;
            }

            return (
                <div className="size-5 rounded-full flex items-center justify-center bg-gray-100 text-gray-500">
                    {approval.email.slice(0, 2).toUpperCase()}
                </div>
            );
        }

        return (
            <img
                className="size-5 rounded-full object-cover"
                src={user.picture}
                alt={user.preferred_username}
            />
        );
    };

    if (loading && sorted.length === 0) {
        return <Loader width="20" height="20" className="text-sky-500" />;
    }

    if (!loading && sorted.length === 0) {
        return <span className="text-gray-500 text-sm">{emptyLabel}</span>;
    }

    return (
        <>
            <button
                type="button"
                className="group flex outline-none md:p-1 cursor-pointer rounded-3xl items-center relative md:hover:bg-gray-50"
                onClick={(event) => {
                    event.stopPropagation();

                    expanded.value = true;

                    if (mobile) {
                        return;
                    }

                    const margin = 8;
                    const r = event.currentTarget.getBoundingClientRect();
                    let top = r.top + r.height;
                    let right = window.innerWidth - r.right;

                    if (top + POPUP_MAX_HEIGHT + margin > window.innerHeight) {
                        top = window.innerHeight - POPUP_MAX_HEIGHT - margin;
                    }

                    if (right + POPUP_WIDTH + margin > window.innerWidth) {
                        right = window.innerWidth - POPUP_WIDTH - margin;
                    }

                    rect.value = {
                        ...rect.peek(),
                        top,
                        right,
                    };
                }}
            >
                {sorted.map((approval, index) => (
                    <ApproverAvatar
                        key={approval.id}
                        approval={approval}
                        index={index}
                    />
                ))}
            </button>
            <Backdrop
                show={expanded}
                rect={rect}
                className={cn(
                    "md:origin-top-right origin-bottom-left flex flex-col gap-y-2 overflow-y-overlay text-sm bg-white rounded-2xl border border-gray-200",
                    mobile
                        ? "w-[calc(100vw-16px)] max-h-[30rem]"
                        : "w-[40rem] max-h-40",
                )}
            >
                <div className="px-4 py-4 flex flex-col gap-y-2">
                    {items.length === 0 && (
                        <div className="text-gray-500 text-center mt-4">
                            No approvals yet
                        </div>
                    )}
                    {items.map((approval) => (
                        <div
                            key={approval.id}
                            className="flex flex-col gap-y-1 border-t border-gray-300 first:border-none pt-2 first:pt-0"
                        >
                            <div className="grid grid-cols-4">
                                <div className="flex items-center gap-x-2 col-span-2">
                                    {renderUserAvatar(approval)}
                                    <span>{getUserFullname(approval)}</span>
                                </div>
                                <span
                                    className={cn(
                                        TEXT_CLASS[getApprovalColor(approval)],
                                    )}
                                >
                                    {approvalKindString(approval)}
                                </span>
                                <span className="text-right">
                                    {amount !== undefined
                                        ? formatIDecimal(new Decimal(amount))
                                        : ""}
                                </span>
                            </div>
                            {approval.comment && (
                                <div className="p-2 rounded-lg bg-gray-100 overflow-x-auto">
                                    {approval.comment}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Backdrop>
        </>
    );
};

export default Approvers;
